import * as Dialog from '@radix-ui/react-dialog';
import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { Button } from './button';

interface ConfirmOptions {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
}

type Ask = (o: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<Ask | null>(null);

/** `const confirm = useConfirm(); if (await confirm({...})) …` — an accessible replacement for window.confirm. */
export const useConfirm = (): Ask => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm needs <ConfirmProvider>');
  return ctx;
};

/** For providers that may render outside <ConfirmProvider> (tests): null when there is none. */
export const useOptionalConfirm = (): Ask | null => useContext(ConfirmContext);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);
  const ask = useCallback<Ask>(
    o =>
      new Promise(resolve => {
        resolver.current = resolve;
        setOptions(o);
      }),
    [],
  );
  const close = (v: boolean) => {
    resolver.current?.(v);
    resolver.current = null;
    setOptions(null);
  };
  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      <Dialog.Root open={!!options} onOpenChange={open => !open && close(false)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
          <Dialog.Content className="fixed top-1/2 left-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 space-y-4 rounded-lg border bg-surface-raised p-5 shadow-lg">
            <Dialog.Title className="font-semibold">{options?.title}</Dialog.Title>
            <Dialog.Description asChild>
              <div className="text-sm text-muted-foreground">{options?.body}</div>
            </Dialog.Description>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => close(false)}>
                {options?.cancelLabel ?? 'Keep it'}
              </Button>
              <Button variant={options?.destructive ? 'danger' : 'primary'} onClick={() => close(true)}>
                {options?.confirmLabel}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </ConfirmContext.Provider>
  );
}
